import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customerApi, CustomerInput } from "@/api/customer.api";

export function useCustomers(params: { page: number; limit: number; search?: string }) {
  return useQuery({
    queryKey: ["customers", params],
    queryFn: () => customerApi.list(params),
    placeholderData: (prev) => prev,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ["customers", id],
    queryFn: () => customerApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCustomerComplaints(id: string | undefined, page = 1, limit = 10) {
  return useQuery({
    queryKey: ["customers", id, "complaints", page, limit],
    queryFn: () => customerApi.getComplaints(id as string, { page, limit }),
    enabled: Boolean(id),
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerInput) => customerApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CustomerInput> }) =>
      customerApi.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customerApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customers"] }),
  });
}
